import type { TechlineCaseEntity } from '@packages/domain/techline/cases';

import { useAuthState } from '@packages/contexts/auth';
import { colors } from '@packages/core/styles';
import { Button, Icon, TextBadge, Typography } from '@packages/ui/shared';
import { ToolTipOverlay } from '@web/components/tool-tip-overlay';
import { configService } from '@web/services/singletons';
import { getBadgeVariantTechline } from '@web/utils';
import { useCallback, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';

import useUserDetailsOrPermissions from '../hooks/useUserDetailsOrPermissions';

/** Minimal profile shape used to check role membership. */
type ProfileWithRole = {
    /** The role identifier for this profile entry. */
    roleId: string;
};

/**
 * Build the NorthStar CRM deep link URL for a given case, selecting the correct
 * Dynamics environment based on the current app environment.
 *
 * @param caseId - the NorthStar/Dynamics CRM case ID
 * @returns the full NorthStar case URL
 */
function getNorthStarCaseUrl(caseId: string): string {
    const env = configService.debugEnvName;

    switch (env) {
        case 'production':
            return `https://subaru.crm.dynamics.com/main.aspx?appid=56dab793-a816-4022-ac3b-e9607aa4015c&forceUCI=1&pagetype=entityrecord&etn=incident&id=${caseId}`;
        case 'stage':
            return `https://subaruqa01.crm.dynamics.com/main.aspx?appid=61f6caea-686f-4ba6-a4c2-66bf7ae956ea&forceUCI=1&pagetype=entityrecord&etn=incident&id=${caseId}`;
        case 'development':
        case 'local':
        default:
            return `https://subarudev01.crm.dynamics.com/main.aspx?appid=06853a56-cc36-40b3-b661-452570b9917a&pagetype=entityrecord&etn=incident&id=${caseId}`;
    }
}

/**
 * Check whether a user profile has the DSQM role.
 *
 * @param profile - a user profile entry with a roleId
 * @returns true if the profile's roleId is 'DSQM'
 */
function isDsqmProfile(profile: ProfileWithRole): boolean {
    return profile?.roleId === 'DSQM';
}

/**
 * Derive the back-navigation handler for the techline header. Uses the provided
 * onBack callback if available, otherwise navigates to the dashboard filtered
 * by the primary retailer.
 *
 * @param onBack - optional explicit back callback
 * @param caseDetails - the case details containing retailer profiles
 * @param navigate - the react-router navigate function
 * @returns a function that handles back navigation
 */
function resolveBackHandler(
    onBack: (() => void) | undefined,
    caseDetails: TechlineCaseEntity | undefined,
    navigate: ReturnType<typeof useNavigate>
): () => void {
    if (onBack) {
        return onBack;
    }
    return () => {
        const primaryRetailerNumber = caseDetails?.retailerProfiles?.find(
            (retailer) => retailer.isPrimary
        )?.retailerNumber;
        navigate(`/techline/dashboard?retailerNumber=${primaryRetailerNumber}`);
    };
}

/** Inline style for the NorthStar link icon's left margin. */
const northStarIconStyle = { marginLeft: 4 };

const useStyles = createUseStyles({
    container: {
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.grayThree}`,
    },
    tooltip: {
        maxWidth: 220,
        '& .tooltip-inner': {
            backgroundColor: colors.brandSecondary,
            borderRadius: 4,
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: '16px',
            maxWidth: 220,
            padding: '4px 8px',
            textAlign: 'left',
            width: 'max-content',
        },
    },
});

/** Props for the TechlineHeader component. */
export type TechlineHeaderProps = {
    /** The full case details object from NorthStar. */
    caseDetails?: TechlineCaseEntity;

    /** The case number identifying this techline case. */
    caseNumber?: string;

    /** Whether the delete button should be disabled. */
    disabledDelete?: boolean;

    /** Whether the save button should be disabled. */
    disabledSave?: boolean;

    /** Whether the submit button should be disabled. */
    disabledSubmit?: boolean;

    /** Whether the primary action button should be disabled. */
    disablePrimaryBtn?: boolean;

    /** Whether the save action is currently in progress. */
    isSaveLoading?: boolean;

    /** Whether the submit action is currently in progress. */
    isSubmitLoading?: boolean;

    /** Callback invoked when the back button is clicked. */
    onBack?: () => void;

    /** Callback invoked when the delete button is clicked. */
    onDelete?: () => void;

    /** Callback invoked when the primary action button is clicked. */
    onPrimaryBtnClick?: () => void;

    /** Callback invoked when the save-and-exit button is clicked. */
    onSaveAndExit?: () => void;

    /** Callback invoked when the submit button is clicked. */
    onSubmit?: () => void;

    /** Tooltip text to display on the submit button when it is disabled. */
    submitTooltipText?: string;
};

/**
 * Sticky header bar for techline case views. Displays the case number,
 * status badge, and contextual action buttons (save, submit, delete, close)
 * based on the current case status and user role.
 *
 * @param props - component props
 * @returns the techline header
 */
export function TechlineHeader({
    caseDetails,
    caseNumber,
    disabledDelete,
    disabledSave,
    disabledSubmit,
    disablePrimaryBtn,
    isSaveLoading,
    isSubmitLoading,
    onBack,
    onDelete,
    onPrimaryBtnClick,
    onSaveAndExit,
    onSubmit,
    submitTooltipText,
}: TechlineHeaderProps) {
    const classes = useStyles();
    const navigate = useNavigate();
    const { account } = useAuthState();
    const {
        permissions: { canCloseCase },
    } = useUserDetailsOrPermissions();

    /** Whether the current user has the DSQM role, which grants NorthStar link visibility. */
    const hasDsqmRole = Array.isArray(account?.profiles) ? account.profiles.some(isDsqmProfile) : false;

    // Handlers

    /** Opens the NorthStar CRM case page in a new tab. */
    const handleOpenNorthStarButtonClick = useCallback(() => {
        if (caseDetails?.caseId) {
            window.open(getNorthStarCaseUrl(caseDetails.caseId), '_blank', 'noopener,noreferrer');
        }
    }, [caseDetails?.caseId]);

    // Derived state

    /** Resolve the back-navigation handler from props or fallback to dashboard navigation. */
    const handleBackButtonClick = useMemo(
        () => resolveBackHandler(onBack, caseDetails, navigate),
        [onBack, caseDetails, navigate]
    );

    /** Determine which right-side actions to render based on case status. */
    const isDraftStatus = caseDetails?.isDraft;
    const isActiveStatus = caseDetails?.isSubmitted && !caseDetails?.isEscalated;

    return (
        <div className={`${classes.container}`}>
            <div className="d-flex align-items-center justify-content-between m-3 flex-row">
                <div className="d-flex align-items-center justify-content-center flex-row">
                    <Button onPress={handleBackButtonClick} variant="link">
                        <Icon name="arrow-back" />
                    </Button>
                    <div className="ml-4 mr-4">
                        <Typography variant="h5">{caseNumber}</Typography>
                    </div>
                    {caseDetails?.techlineStatusCode && (
                        <TextBadge variant={getBadgeVariantTechline(caseDetails.techlineStatusCode)}>
                            {caseDetails.techlineStatusCode}
                        </TextBadge>
                    )}
                </div>
                {isDraftStatus && (
                    <div className="d-flex align-items-center justify-content-center flex-row">
                        <Typography color="textDarkSecondary" variant="labelRegular">
                            Saved on {caseDetails?.modifiedOnDescription}
                        </Typography>
                        <div className="ml-4">
                            <Button
                                disabled={disabledSave}
                                isLoading={isSaveLoading}
                                onPress={onSaveAndExit}
                                variant="primary-blue-outline"
                            >
                                <Typography color="blueOne">Save and exit</Typography>
                            </Button>
                        </div>
                        <div className="ml-4">
                            <ToolTipOverlay
                                className={classes.tooltip}
                                text={disabledSubmit ? submitTooltipText : undefined}
                                tooltipId={'draft-submit-tooltip'}
                            >
                                <Button
                                    disabled={disabledSubmit}
                                    isLoading={isSubmitLoading}
                                    onPress={onSubmit}
                                    title={'Submit'}
                                    variant="primary"
                                />
                            </ToolTipOverlay>
                        </div>
                        <div className="ml-4">
                            <Button disabled={disabledDelete} onPress={onDelete} variant="ghost-gray">
                                <Icon color="blueOne" name="trash" />
                            </Button>
                        </div>
                    </div>
                )}
                {isActiveStatus && (
                    <div className="d-flex align-items-center justify-content-center flex-row">
                        <Typography color="textDarkSecondary" variant="labelRegular">
                            Saved on {caseDetails?.modifiedOnDescription}
                        </Typography>
                        {hasDsqmRole && caseDetails?.caseId && (
                            <div className="ml-4">
                                <Button onPress={handleOpenNorthStarButtonClick} variant="primary-blue-outline">
                                    <div className="d-flex align-items-center flex-row">
                                        <Typography color="blueOne">Open in NorthStar</Typography>
                                        <Icon color="blueOne" name="open-in-new" size={16} style={northStarIconStyle} />
                                    </div>
                                </Button>
                            </div>
                        )}
                        {canCloseCase && (
                            <div className="ml-4">
                                <ToolTipOverlay
                                    className={classes.tooltip}
                                    text={submitTooltipText}
                                    tooltipId={'submit-tooltip'}
                                >
                                    <Button
                                        disabled={disablePrimaryBtn}
                                        onPress={onPrimaryBtnClick}
                                        title={'Close case'}
                                        variant="primary"
                                    />
                                </ToolTipOverlay>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
